import { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  onSnapshot, 
  addDoc, 
  deleteDoc, 
  doc, 
  updateDoc,
  serverTimestamp,
  orderBy
} from 'firebase/firestore';
import { db } from '../../lib/firebase';

export function useFirestoreCollection<T>(collectionName: string) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const q = query(collection(db, collectionName), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const items: any[] = [];
      querySnapshot.forEach((doc) => {
        items.push({ id: doc.id, ...doc.data() });
      });
      setData(items);
      setLoading(false);
    }, (err) => {
      console.error(`Error fetching ${collectionName}:`, err);
      setError(err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [collectionName]);

  const add = async (newData: any) => {
    try {
      await addDoc(collection(db, collectionName), {
        ...newData,
        createdAt: serverTimestamp()
      });
    } catch (err) {
      console.error(`Error adding to ${collectionName}:`, err);
      throw err;
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteDoc(doc(db, collectionName, id));
    } catch (err) {
      console.error(`Error deleting from ${collectionName}:`, err);
      throw err;
    }
  };

  const update = async (id: string, updatedFields: any) => {
    try {
      await updateDoc(doc(db, collectionName, id), updatedFields);
    } catch (err) {
      console.error(`Error updating ${collectionName}:`, err);
      throw err;
    }
  };

  return { data, loading, error, add, remove, update };
}

export function useFirestoreDoc<T>(collectionName: string, docId: string) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, collectionName, docId), (docSnap) => {
      if (docSnap.exists()) {
        setData({ id: docSnap.id, ...docSnap.data() } as T);
      } else {
        setData(null);
      }
      setLoading(false);
    }, (err) => {
      console.error(`Error fetching ${collectionName}/${docId}:`, err);
      setError(err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [collectionName, docId]);

  const update = async (updatedFields: Partial<T>) => {
    try {
      await updateDoc(doc(db, collectionName, docId), updatedFields as any);
    } catch (err) {
      console.error(`Error updating ${collectionName}/${docId}:`, err);
      throw err;
    }
  };

  const set = async (newData: T) => {
    try {
      const { id, ...dataToSave } = newData as any;
      await updateDoc(doc(db, collectionName, docId), dataToSave);
    } catch (err) {
      // If doc doesn't exist, try to set it
      try {
        const { id, ...dataToSave } = newData as any;
        const { setDoc } = await import('firebase/firestore');
        await setDoc(doc(db, collectionName, docId), dataToSave);
      } catch (innerErr) {
        throw innerErr;
      }
    }
  };

  return { data, loading, error, update, set };
}
